<template>
  <v-card>
    <!-- ===================================================== -->
    <!-- CARREGANDO -->
    <!-- ===================================================== -->

    <v-card-text v-if="loading">
      <div class="d-flex justify-center py-8">
        <v-progress-circular indeterminate color="primary" />
      </div>
    </v-card-text>

    <!-- ===================================================== -->
    <!-- SEM REGISTROS -->
    <!-- ===================================================== -->

    <v-card-text v-else-if="!propertyRegistries.length">
      <div class="text-center py-8">
        <v-icon size="48" color="grey lighten-1">
          mdi-file-document-outline
        </v-icon>

        <div class="mt-3 grey--text">
          {{ __("Nenhuma caderneta predial encontrada") }}
        </div>
      </div>
    </v-card-text>

    <!-- ===================================================== -->
    <!-- REGISTROS -->
    <!-- ===================================================== -->

    <template v-else>
      <!-- =================================================== -->
      <!-- SELEÇÃO DE CADERNETAS -->
      <!-- =================================================== -->

      <v-card-text v-if="propertyRegistries.length > 1" class="pb-2">
        <div class="d-flex align-center mb-3">
          <div class="text-subtitle-1 font-weight-medium flex-grow-1">
            {{ __("Cadernetas prediais") }}
          </div>

          <v-chip small>
            {{ propertyRegistries.length }}
          </v-chip>
        </div>

        <v-chip-group
          v-model="selectedRegistryId"
          mandatory
          active-class="primary--text"
        >
          <v-chip
            v-for="registry in propertyRegistries"
            :key="registry.id"
            :value="registry.id"
            outlined
            :disabled="saving || editingSection !== null"
          >
            <v-icon left small> mdi-file-document-outline </v-icon>

            {{
              registry.attachment
                ? registry.attachment.name
                : __("Caderneta predial")
            }}
          </v-chip>
        </v-chip-group>
      </v-card-text>

      <v-divider v-if="propertyRegistries.length > 1" />

      <v-divider />

      <!-- =================================================== -->
      <!-- REGISTRO SELECIONADO -->
      <!-- =================================================== -->

      <template v-if="propertyRegistry">
        <!-- Cabeçalho -->

        <v-card-title>
          <v-avatar
            size="40"
            color="grey lighten-4"
            class="mr-3"
            style="cursor: pointer"
            @click="handleOpenDocument"
          >
            <v-icon color="primary"> mdi-file-document-outline </v-icon>
          </v-avatar>

          <div
            class="flex-grow-1"
            style="cursor: pointer"
            :title="__('Abrir documento')"
            @click="handleOpenDocument"
          >
            <div class="font-weight-medium">
              {{ attachmentName }}
            </div>

            <div class="text-caption grey--text">
              {{ __("Documento associado ao imóvel") }}
            </div>
          </div>

          <v-chip
            small
            class="mr-2"
            :color="statusColor"
            :text-color="statusTextColor"
          >
            {{ statusLabel }}
          </v-chip>

          <ScopeProvider scope="leads.property_registry.update">
            <v-btn
              v-if="propertyRegistry.status === 'failed'"
              text
              small
              color="primary"
              :loading="reprocessing"
              @click="handleReprocess"
            >
              <v-icon left small> mdi-refresh </v-icon>

              {{ __("Reprocessar") }}
            </v-btn>
          </ScopeProvider>

          <ScopeProvider scope="leads.property_registry.delete">
            <!-- Excluir -->
            <v-btn
              text
              small
              color="red"
              :loading="deleting"
              :disabled="editing || saving"
              @click="handleDelete"
            >
              <v-icon left small> mdi-delete </v-icon>

              {{ __("Excluir") }}
            </v-btn>
          </ScopeProvider>

          <!-- Recarregar -->
          <v-btn
            icon
            :loading="loading"
            :disabled="editing || saving"
            :title="__('Recarregar')"
            @click="fetchPropertyRegistry"
          >
            <v-icon> mdi-refresh </v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <!-- ================================================= -->
        <!-- ERRO -->
        <!-- ================================================= -->

        <v-card-text v-if="propertyRegistry.status === 'failed'">
          <v-alert type="error" outlined class="mb-0">
            {{ propertyRegistry.error_message }}
          </v-alert>
        </v-card-text>

        <!-- ================================================= -->
        <!-- INFORMAÇÕES -->
        <!-- ================================================= -->

        <v-card-text>
          <v-expansion-panels v-model="openPanels" multiple>
            <!-- ================================================= -->
            <!-- IDENTIFICAÇÃO -->
            <!-- ================================================= -->

            <v-expansion-panel>
              <v-expansion-panel-header>
                <div class="property-registry-section-title">
                  <v-icon> mdi-identifier </v-icon>

                  <span>
                    {{ __("Identificação") }}
                  </span>

                  <ScopeProvider scope="leads.property_registry.update">
                    <v-btn
                      v-if="editingSection !== 'identification'"
                      text
                      small
                      color="primary"
                      class="mr-2"
                      :disabled="saving || editingSection !== null"
                      @click.stop="startEditingSection('identification')"
                    >
                      <v-icon left small> mdi-pencil </v-icon>

                      {{ __("Alterar") }}
                    </v-btn>

                    <v-btn
                      v-else
                      text
                      small
                      color="grey darken-1"
                      :disabled="saving"
                      @click.stop="resetSection('identification')"
                    >
                      <v-icon left small> mdi-close </v-icon>

                      {{ __("Cancelar alteração") }}
                    </v-btn>
                  </ScopeProvider>
                </div>
              </v-expansion-panel-header>

              <v-expansion-panel-content>
                <v-row dense>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-if="editingSection === 'identification'"
                      v-model="editForm.identification.district.name"
                      :label="__('Distrito')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Distrito") }}
                      </div>

                      <div>
                        {{
                          identification.district
                            ? identification.district.name
                            : "-"
                        }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-if="editingSection === 'identification'"
                      v-model="editForm.identification.municipality.name"
                      :label="__('Concelho')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Concelho") }}
                      </div>

                      <div>
                        {{
                          identification.municipality
                            ? identification.municipality.name
                            : "-"
                        }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-if="editingSection === 'identification'"
                      v-model="editForm.identification.parish.name"
                      :label="__('Freguesia')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Freguesia") }}
                      </div>

                      <div>
                        {{
                          identification.parish
                            ? identification.parish.name
                            : "-"
                        }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      v-if="editingSection === 'identification'"
                      v-model="editForm.identification.property_article_number"
                      :label="__('Artigo matricial')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Artigo matricial") }}
                      </div>

                      <div>
                        {{ identification.property_article_number || "-" }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      v-if="editingSection === 'identification'"
                      v-model="
                        editForm.identification.property_identification_number
                      "
                      :label="__('Número de identificação predial')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Número de identificação predial") }}
                      </div>

                      <div>
                        {{
                          identification.property_identification_number || "-"
                        }}
                      </div>
                    </template>
                  </v-col>
                </v-row>

                <ScopeProvider scope="leads.property_registry.update">
                  <div
                    v-if="editingSection === 'identification'"
                    class="section-actions"
                  >
                    <v-btn
                      text
                      small
                      class="mr-2"
                      :disabled="saving"
                      @click="resetSection('identification')"
                    >
                      {{ __("Cancelar") }}
                    </v-btn>

                    <v-btn
                      small
                      color="primary"
                      :loading="savingSection === 'identification'"
                      :disabled="saving"
                      @click="saveSection('identification')"
                    >
                      <v-icon left small> mdi-content-save </v-icon>

                      {{ __("Guardar alterações") }}
                    </v-btn>
                  </div>
                </ScopeProvider>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- ================================================= -->
            <!-- LOCALIZAÇÃO -->
            <!-- ================================================= -->

            <v-expansion-panel>
              <v-expansion-panel-header>
                <div class="property-registry-section-title">
                  <v-icon> mdi-map-marker </v-icon>

                  <span>
                    {{ __("Localização") }}
                  </span>

                  <ScopeProvider scope="leads.property_registry.update">
                    <v-btn
                      v-if="editingSection !== 'location'"
                      text
                      small
                      color="primary"
                      class="mr-2"
                      :disabled="saving || editingSection !== null"
                      @click.stop="startEditingSection('location')"
                    >
                      <v-icon left small> mdi-pencil </v-icon>

                      {{ __("Alterar") }}
                    </v-btn>

                    <v-btn
                      v-else
                      text
                      small
                      color="grey darken-1"
                      :disabled="saving"
                      @click.stop="resetSection('location')"
                    >
                      <v-icon left small> mdi-close </v-icon>

                      {{ __("Cancelar alteração") }}
                    </v-btn>
                  </ScopeProvider>
                </div>
              </v-expansion-panel-header>

              <v-expansion-panel-content>
                <v-row dense>
                  <v-col cols="12" md="8">
                    <v-text-field
                      v-if="editingSection === 'location'"
                      v-model="editForm.location.street"
                      :label="__('Morada')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Morada") }}
                      </div>

                      <div>
                        {{ location.street || "-" }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-if="editingSection === 'location'"
                      v-model="editForm.location.street_number"
                      :label="__('Número')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Número") }}
                      </div>

                      <div>
                        {{ location.street_number || "-" }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-if="editingSection === 'location'"
                      v-model="editForm.location.place"
                      :label="__('Localidade')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Localidade") }}
                      </div>

                      <div>
                        {{ location.place || "-" }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-if="editingSection === 'location'"
                      v-model="editForm.location.postal_code"
                      :label="__('Código postal')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Código postal") }}
                      </div>

                      <div>
                        {{ location.postal_code || "-" }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-if="editingSection === 'location'"
                      v-model="editForm.location.floor"
                      :label="__('Andar')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Andar") }}
                      </div>

                      <div>
                        {{ location.floor || "-" }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-if="editingSection === 'location'"
                      v-model="editForm.location.postal_locality"
                      :label="__('Localidade postal')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Localidade postal") }}
                      </div>

                      <div>
                        {{ location.postal_locality || "-" }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-if="editingSection === 'location'"
                      v-model="editForm.location.coordinates.x"
                      :label="__('Coordenada X')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Coordenada X") }}
                      </div>

                      <div>
                        {{
                          valuation.coordinates &&
                          valuation.coordinates.x != null
                            ? valuation.coordinates.x
                            : "-"
                        }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-if="editingSection === 'location'"
                      v-model="editForm.location.coordinates.y"
                      :label="__('Coordenada Y')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Coordenada Y") }}
                      </div>

                      <div>
                        {{
                          valuation.coordinates &&
                          valuation.coordinates.y != null
                            ? valuation.coordinates.y
                            : "-"
                        }}
                      </div>
                    </template>
                  </v-col>
                </v-row>

                <ScopeProvider scope="leads.property_registry.update">
                  <div
                    v-if="editingSection === 'location'"
                    class="section-actions"
                  >
                    <v-btn
                      text
                      small
                      class="mr-2"
                      :disabled="saving"
                      @click="resetSection('location')"
                    >
                      {{ __("Cancelar") }}
                    </v-btn>

                    <v-btn
                      small
                      color="primary"
                      :loading="savingSection === 'location'"
                      :disabled="saving"
                      @click="saveSection('location')"
                    >
                      <v-icon left small> mdi-content-save </v-icon>

                      {{ __("Guardar alterações") }}
                    </v-btn>
                  </div>
                </ScopeProvider>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- ================================================= -->
            <!-- DESCRIÇÃO -->
            <!-- ================================================= -->

            <v-expansion-panel>
              <v-expansion-panel-header>
                <div class="property-registry-section-title">
                  <v-icon> mdi-home-outline </v-icon>

                  <span>
                    {{ __("Descrição do imóvel") }}
                  </span>

                  <ScopeProvider scope="leads.property_registry.update">
                    <v-btn
                      v-if="editingSection !== 'description'"
                      text
                      small
                      color="primary"
                      class="mr-2"
                      :disabled="saving || editingSection !== null"
                      @click.stop="startEditingSection('description')"
                    >
                      <v-icon left small> mdi-pencil </v-icon>

                      {{ __("Alterar") }}
                    </v-btn>

                    <v-btn
                      v-else
                      text
                      small
                      color="grey darken-1"
                      :disabled="saving"
                      @click.stop="resetSection('description')"
                    >
                      <v-icon left small> mdi-close </v-icon>

                      {{ __("Cancelar alteração") }}
                    </v-btn>
                  </ScopeProvider>
                </div>
              </v-expansion-panel-header>

              <v-expansion-panel-content>
                <v-row dense>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-if="editingSection === 'description'"
                      v-model="editForm.description.property_type"
                      :label="__('Tipo de prédio')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Tipo de prédio") }}
                      </div>

                      <div>
                        {{ description.property_type || "-" }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      v-if="editingSection === 'description'"
                      v-model="editForm.description.use"
                      :label="__('Utilização')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Utilização") }}
                      </div>

                      <div>
                        {{ description.use || "-" }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12">
                    <v-textarea
                      v-if="editingSection === 'description'"
                      v-model="editForm.description.property_description"
                      :label="__('Descrição')"
                      outlined
                      dense
                      rows="3"
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Descrição") }}
                      </div>

                      <div>
                        {{ description.property_description || "-" }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-if="editingSection === 'description'"
                      v-model="editForm.description.number_of_floors"
                      :label="__('Número de pisos')"
                      type="number"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Número de pisos") }}
                      </div>

                      <div>
                        {{
                          description.number_of_floors != null
                            ? description.number_of_floors
                            : "-"
                        }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="8">
                    <v-text-field
                      v-if="editingSection === 'description'"
                      v-model="editForm.description.typology_or_divisions"
                      :label="__('Tipologia / Divisões')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Tipologia / Divisões") }}
                      </div>

                      <div>
                        {{ description.typology_or_divisions || "-" }}
                      </div>
                    </template>
                  </v-col>
                </v-row>

                <ScopeProvider scope="leads.property_registry.update">
                  <div
                    v-if="editingSection === 'location'"
                    class="section-actions"
                  >
                    <v-btn
                      text
                      small
                      class="mr-2"
                      :disabled="saving"
                      @click="resetSection('location')"
                    >
                      {{ __("Cancelar") }}
                    </v-btn>

                    <v-btn
                      small
                      color="primary"
                      :loading="savingSection === 'location'"
                      :disabled="saving"
                      @click="saveSection('location')"
                    >
                      <v-icon left small> mdi-content-save </v-icon>

                      {{ __("Guardar alterações") }}
                    </v-btn>
                  </div>
                </ScopeProvider>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- ================================================= -->
            <!-- ÁREAS -->
            <!-- ================================================= -->

            <v-expansion-panel>
              <v-expansion-panel-header>
                <div class="property-registry-section-title">
                  <v-icon> mdi-ruler-square </v-icon>

                  <span>
                    {{ __("Áreas") }}
                  </span>

                  <ScopeProvider scope="leads.property_registry.update">
                    <v-btn
                      v-if="editingSection !== 'areas'"
                      text
                      small
                      color="primary"
                      class="mr-2"
                      :disabled="saving || editingSection !== null"
                      @click.stop="startEditingSection('areas')"
                    >
                      <v-icon left small> mdi-pencil </v-icon>

                      {{ __("Alterar") }}
                    </v-btn>

                    <v-btn
                      v-else
                      text
                      small
                      color="grey darken-1"
                      :disabled="saving"
                      @click.stop="resetSection('areas')"
                    >
                      <v-icon left small> mdi-close </v-icon>

                      {{ __("Cancelar alteração") }}
                    </v-btn>
                  </ScopeProvider>
                </div>
              </v-expansion-panel-header>

              <v-expansion-panel-content>
                <v-row dense>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-if="editingSection === 'areas'"
                      v-model="editForm.areas.total_land_area"
                      :label="__('Área total do terreno')"
                      type="number"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Área total do terreno") }}
                      </div>

                      <div>
                        {{
                          areas.total_land_area != null
                            ? areas.total_land_area + " " + (areas.unit || "")
                            : "-"
                        }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-if="editingSection === 'areas'"
                      v-model="editForm.areas.building_footprint_area"
                      :label="__('Área de implantação')"
                      type="number"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Área de implantação") }}
                      </div>

                      <div>
                        {{
                          areas.building_footprint_area != null
                            ? areas.building_footprint_area +
                              " " +
                              (areas.unit || "")
                            : "-"
                        }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-if="editingSection === 'areas'"
                      v-model="editForm.areas.gross_construction_area"
                      :label="__('Área bruta de construção')"
                      type="number"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Área bruta de construção") }}
                      </div>

                      <div>
                        {{
                          areas.gross_construction_area != null
                            ? areas.gross_construction_area +
                              " " +
                              (areas.unit || "")
                            : "-"
                        }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-if="editingSection === 'areas'"
                      v-model="editForm.areas.gross_dependent_area"
                      :label="__('Área bruta dependente')"
                      type="number"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Área bruta dependente") }}
                      </div>

                      <div>
                        {{
                          areas.gross_dependent_area != null
                            ? areas.gross_dependent_area +
                              " " +
                              (areas.unit || "")
                            : "-"
                        }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-if="editingSection === 'areas'"
                      v-model="editForm.areas.gross_private_area"
                      :label="__('Área bruta privativa')"
                      type="number"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Área bruta privativa") }}
                      </div>

                      <div>
                        {{
                          areas.gross_private_area != null
                            ? areas.gross_private_area +
                              " " +
                              (areas.unit || "")
                            : "-"
                        }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-if="editingSection === 'areas'"
                      v-model="editForm.areas.unit"
                      :label="__('Unidade')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Unidade") }}
                      </div>

                      <div>
                        {{ areas.unit || "-" }}
                      </div>
                    </template>
                  </v-col>
                </v-row>

                <ScopeProvider scope="leads.property_registry.update">
                  <div
                    v-if="editingSection === 'areas'"
                    class="section-actions"
                  >
                    <v-btn
                      text
                      small
                      class="mr-2"
                      :disabled="saving"
                      @click="resetSection('areas')"
                    >
                      {{ __("Cancelar") }}
                    </v-btn>

                    <v-btn
                      small
                      color="primary"
                      :loading="savingSection === 'areas'"
                      :disabled="saving"
                      @click="saveSection('areas')"
                    >
                      <v-icon left small> mdi-content-save </v-icon>

                      {{ __("Guardar alterações") }}
                    </v-btn>
                  </div>
                </ScopeProvider>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- ================================================= -->
            <!-- AVALIAÇÃO -->
            <!-- ================================================= -->

            <v-expansion-panel>
              <v-expansion-panel-header>
                <div class="property-registry-section-title">
                  <v-icon> mdi-cash </v-icon>

                  <span>
                    {{ __("Avaliação") }}
                  </span>

                  <ScopeProvider scope="leads.property_registry.update">
                    <v-btn
                      v-if="editingSection !== 'valuation'"
                      text
                      small
                      color="primary"
                      class="mr-2"
                      :disabled="saving || editingSection !== null"
                      @click.stop="startEditingSection('valuation')"
                    >
                      <v-icon left small> mdi-pencil </v-icon>

                      {{ __("Alterar") }}
                    </v-btn>

                    <v-btn
                      v-else
                      text
                      small
                      color="grey darken-1"
                      :disabled="saving"
                      @click.stop="resetSection('valuation')"
                    >
                      <v-icon left small> mdi-close </v-icon>

                      {{ __("Cancelar alteração") }}
                    </v-btn>
                  </ScopeProvider>
                </div>
              </v-expansion-panel-header>

              <v-expansion-panel-content>
                <v-row dense>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-if="editingSection === 'valuation'"
                      v-model="editForm.valuation.matrix_registration_year"
                      :label="__('ano de inscrição matricial')"
                      type="number"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Ano de inscrição matricial") }}
                      </div>

                      <div>
                        {{
                          valuation.matrix_registration_year != null
                            ? valuation.matrix_registration_year
                            : "-"
                        }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-if="editingSection === 'valuation'"
                      v-model="
                        editForm.valuation.current_tax_asset_value.amount
                      "
                      :label="__('Valor patrimonial tributário')"
                      type="number"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Valor patrimonial tributário") }}
                      </div>

                      <div>
                        {{ taxAssetValue }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-if="editingSection === 'valuation'"
                      v-model="editForm.valuation.valuation_determination_year"
                      :label="__('Ano da avaliação')"
                      type="number"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Ano da avaliação") }}
                      </div>

                      <div>
                        {{
                          valuation.valuation_determination_year != null
                            ? valuation.valuation_determination_year
                            : "-"
                        }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      v-if="editingSection === 'valuation'"
                      v-model="editForm.valuation.location_coefficient_type"
                      :label="__('Coeficiente de localização')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Coeficiente de localização") }}
                      </div>

                      <div>
                        {{ valuation.location_coefficient_type || "-" }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      v-if="editingSection === 'valuation'"
                      v-model="editForm.valuation.coordinates.reference_system"
                      :label="__('Sistema de referência')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Sistema de referência") }}
                      </div>

                      <div>
                        {{
                          valuation.coordinates &&
                          valuation.coordinates.reference_system
                            ? valuation.coordinates.reference_system
                            : "-"
                        }}
                      </div>
                    </template>
                  </v-col>
                </v-row>

                <ScopeProvider scope="leads.property_registry.update">
                  <div
                    v-if="editingSection === 'valuation'"
                    class="section-actions"
                  >
                    <v-btn
                      text
                      small
                      class="mr-2"
                      :disabled="saving"
                      @click="resetSection('valuation')"
                    >
                      {{ __("Cancelar") }}
                    </v-btn>

                    <v-btn
                      small
                      color="primary"
                      :loading="savingSection === 'valuation'"
                      :disabled="saving"
                      @click="saveSection('valuation')"
                    >
                      <v-icon left small> mdi-content-save </v-icon>

                      {{ __("Guardar alterações") }}
                    </v-btn>
                  </div>
                </ScopeProvider>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- ================================================= -->
            <!-- TITULARES -->
            <!-- ================================================= -->

            <v-expansion-panel>
              <v-expansion-panel-header>
                <div class="property-registry-section-title">
                  <v-icon> mdi-account-group </v-icon>

                  <span>
                    {{ __("Titulares") }}
                  </span>

                  <v-chip v-if="holders.length" x-small class="ml-2">
                    {{ holders.length }}
                  </v-chip>

                  <ScopeProvider scope="leads.property_registry.update">
                    <v-btn
                      v-if="editingSection !== 'holders'"
                      text
                      small
                      color="primary"
                      class="mr-2"
                      :disabled="saving || editingSection !== null"
                      @click.stop="startEditingSection('holders')"
                    >
                      <v-icon left small> mdi-pencil </v-icon>

                      {{ __("Alterar") }}
                    </v-btn>

                    <v-btn
                      v-else
                      text
                      small
                      color="grey darken-1"
                      :disabled="saving"
                      @click.stop="resetSection('holders')"
                    >
                      <v-icon left small> mdi-close </v-icon>

                      {{ __("Cancelar alteração") }}
                    </v-btn>
                  </ScopeProvider>
                </div>
              </v-expansion-panel-header>

              <v-expansion-panel-content>
                <template v-if="editingSection === 'holders'">
                  <v-card
                    v-for="(holder, index) in editForm.holders"
                    :key="'edit-holder-' + index"
                    outlined
                    class="mb-3"
                  >
                    <v-card-text>
                      <v-row dense>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="holder.name"
                            :label="__('Nome')"
                            outlined
                            dense
                          />
                        </v-col>

                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="holder.tax_identification_number"
                            :label="__('Número de identificação fiscal')"
                            outlined
                            dense
                          />
                        </v-col>

                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="holder.holder_type"
                            :label="__('Tipo de titularidade')"
                            outlined
                            dense
                          />
                        </v-col>

                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="holder.share.raw_value"
                            :label="__('Quota')"
                            outlined
                            dense
                          />
                        </v-col>

                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="holder.document"
                            :label="__('Documento')"
                            outlined
                            dense
                          />
                        </v-col>

                        <v-col cols="12">
                          <v-text-field
                            v-model="holder.entity"
                            :label="__('Entidade')"
                            outlined
                            dense
                          />
                        </v-col>

                        <v-col cols="12">
                          <v-text-field
                            v-model="holder.address.full"
                            :label="__('Morada')"
                            outlined
                            dense
                          />
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </template>

                <template v-else>
                  <v-card
                    v-for="holder in holders"
                    :key="holder.id"
                    outlined
                    class="mb-3"
                  >
                    <v-card-text>
                      <div class="font-weight-medium">
                        {{ holder.name || "-" }}
                      </div>

                      <div class="text-caption grey--text">
                        {{ holder.tax_identification_number || "-" }}
                      </div>

                      <v-divider class="my-3" />

                      <v-row dense>
                        <v-col cols="12" md="4">
                          <div class="text-caption grey--text">
                            {{ __("Tipo de titularidade") }}
                          </div>

                          <div>
                            {{ holder.holder_type || "-" }}
                          </div>
                        </v-col>

                        <v-col cols="12" md="4">
                          <div class="text-caption grey--text">
                            {{ __("Quota") }}
                          </div>

                          <div>
                            {{ holder.share ? holder.share.raw_value : "-" }}
                          </div>
                        </v-col>

                        <v-col cols="12" md="4">
                          <div class="text-caption grey--text">
                            {{ __("Documento") }}
                          </div>

                          <div>
                            {{ holder.document || "-" }}
                          </div>
                        </v-col>

                        <v-col cols="12">
                          <div class="text-caption grey--text">
                            {{ __("Entidade") }}
                          </div>

                          <div>
                            {{ holder.entity || "-" }}
                          </div>
                        </v-col>

                        <v-col cols="12">
                          <div class="text-caption grey--text">
                            {{ __("Morada") }}
                          </div>

                          <div>
                            {{ holder.address ? holder.address.full : "-" }}
                          </div>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>

                  <div
                    v-if="!holders.length"
                    class="grey--text text-center py-4"
                  >
                    {{ __("Nenhum titular encontrado") }}
                  </div>
                </template>

                <ScopeProvider scope="leads.property_registry.update">
                  <div
                    v-if="editingSection === 'holders'"
                    class="section-actions"
                  >
                    <v-btn
                      text
                      small
                      class="mr-2"
                      :disabled="saving"
                      @click="resetSection('holders')"
                    >
                      {{ __("Cancelar") }}
                    </v-btn>

                    <v-btn
                      small
                      color="primary"
                      :loading="savingSection === 'holders'"
                      :disabled="saving"
                      @click="saveSection('holders')"
                    >
                      <v-icon left small> mdi-content-save </v-icon>

                      {{ __("Guardar alterações") }}
                    </v-btn>
                  </div>
                </ScopeProvider>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- ================================================= -->
            <!-- INFORMAÇÕES ADICIONAIS -->
            <!-- ================================================= -->

            <v-expansion-panel>
              <v-expansion-panel-header>
                <div class="property-registry-section-title">
                  <v-icon> mdi-information-outline </v-icon>

                  <span>
                    {{ __("Informações adicionais") }}
                  </span>

                  <ScopeProvider scope="leads.property_registry.update">
                    <v-btn
                      v-if="editingSection !== 'additional_information'"
                      text
                      small
                      color="primary"
                      class="mr-2"
                      :disabled="saving || editingSection !== null"
                      @click.stop="
                        startEditingSection('additional_information')
                      "
                    >
                      <v-icon left small> mdi-pencil </v-icon>

                      {{ __("Alterar") }}
                    </v-btn>

                    <v-btn
                      v-else
                      text
                      small
                      color="grey darken-1"
                      :disabled="saving"
                      @click.stop="resetSection('additional_information')"
                    >
                      <v-icon left small> mdi-close </v-icon>

                      {{ __("Cancelar alteração") }}
                    </v-btn>
                  </ScopeProvider>
                </div>
              </v-expansion-panel-header>

              <v-expansion-panel-content>
                <v-row dense>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-if="editingSection === 'additional_information'"
                      v-model="editForm.additional_information.document_type"
                      :label="__('Tipo de documento')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Tipo de documento") }}
                      </div>

                      <div>
                        {{ additionalInformation.document_type || "-" }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-if="editingSection === 'additional_information'"
                      v-model="editForm.additional_information.tax_office.name"
                      :label="__('Serviço de finanças')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Serviço de finanças") }}
                      </div>

                      <div>
                        {{
                          additionalInformation.tax_office
                            ? additionalInformation.tax_office.name
                            : "-"
                        }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-if="editingSection === 'additional_information'"
                      v-model="editForm.additional_information.tax_office.code"
                      :label="__('Código do serviço de finanças')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Código do serviço de finanças") }}
                      </div>

                      <div>
                        {{
                          additionalInformation.tax_office
                            ? additionalInformation.tax_office.code
                            : "-"
                        }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-if="editingSection === 'additional_information'"
                      v-model="
                        editForm.additional_information.property_reference
                      "
                      :label="__('Referência do prédio')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Referência do prédio") }}
                      </div>

                      <div>
                        {{ additionalInformation.property_reference || "-" }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-if="editingSection === 'additional_information'"
                      v-model="
                        editForm.additional_information.imi_model_1_number
                      "
                      :label="__('Modelo 1 do IMI')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Modelo 1 do IMI") }}
                      </div>

                      <div>
                        {{ additionalInformation.imi_model_1_number || "-" }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-if="editingSection === 'additional_information'"
                      v-model="
                        editForm.additional_information
                          .imi_model_1_submission_date
                      "
                      :label="__('Data de submissão do modelo 1')"
                      type="date"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Data de submissão do modelo 1") }}
                      </div>

                      <div>
                        {{
                          formatDate(
                            additionalInformation.imi_model_1_submission_date
                          )
                        }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-if="editingSection === 'additional_information'"
                      v-model="
                        editForm.additional_information.valuation_record_number
                      "
                      :label="__('Número de avaliação')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Número de avaliação") }}
                      </div>

                      <div>
                        {{
                          additionalInformation.valuation_record_number || "-"
                        }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-if="editingSection === 'additional_information'"
                      v-model="editForm.additional_information.valuation_date"
                      :label="__('Data da avaliação')"
                      type="date"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Data da avaliação") }}
                      </div>

                      <div>
                        {{ formatDate(additionalInformation.valuation_date) }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-if="editingSection === 'additional_information'"
                      v-model="
                        editForm.additional_information.document_issue_date
                      "
                      :label="__('Data de emissão')"
                      type="date"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Data de emissão") }}
                      </div>

                      <div>
                        {{
                          formatDate(additionalInformation.document_issue_date)
                        }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      v-if="editingSection === 'additional_information'"
                      v-model="
                        editForm.additional_information
                          .issuer_tax_identification_number
                      "
                      :label="__('Número de identificação fiscal do emitente')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Número de identificação fiscal do emitente") }}
                      </div>

                      <div>
                        {{
                          additionalInformation.issuer_tax_identification_number ||
                          "-"
                        }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      v-if="editingSection === 'additional_information'"
                      v-model="editForm.additional_information.validation_code"
                      :label="__('Código de validação')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Código de validação") }}
                      </div>

                      <div>
                        {{ additionalInformation.validation_code || "-" }}
                      </div>
                    </template>
                  </v-col>
                </v-row>

                <ScopeProvider scope="leads.property_registry.update">
                  <div
                    v-if="editingSection === 'additional_information'"
                    class="section-actions"
                  >
                    <v-btn
                      text
                      small
                      class="mr-2"
                      :disabled="saving"
                      @click="resetSection('additional_information')"
                    >
                      {{ __("Cancelar") }}
                    </v-btn>

                    <v-btn
                      small
                      color="primary"
                      :loading="savingSection === 'additional_information'"
                      :disabled="saving"
                      @click="saveSection('additional_information')"
                    >
                      <v-icon left small> mdi-content-save </v-icon>

                      {{ __("Guardar alterações") }}
                    </v-btn>
                  </div>
                </ScopeProvider>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- ================================================= -->
            <!-- CONFRONTAÇÕES -->
            <!-- ================================================= -->

            <v-expansion-panel>
              <v-expansion-panel-header>
                <div class="property-registry-section-title">
                  <v-icon> mdi-compass </v-icon>

                  <span>
                    {{ __("Confrontações") }}
                  </span>

                  <ScopeProvider scope="leads.property_registry.update">
                    <v-btn
                      v-if="editingSection !== 'boundaries'"
                      text
                      small
                      color="primary"
                      class="mr-2"
                      :disabled="saving || editingSection !== null"
                      @click.stop="startEditingSection('boundaries')"
                    >
                      <v-icon left small> mdi-pencil </v-icon>

                      {{ __("Alterar") }}
                    </v-btn>

                    <v-btn
                      v-else
                      text
                      small
                      color="grey darken-1"
                      :disabled="saving"
                      @click.stop="resetSection('boundaries')"
                    >
                      <v-icon left small> mdi-close </v-icon>

                      {{ __("Cancelar alteração") }}
                    </v-btn>
                  </ScopeProvider>
                </div>
              </v-expansion-panel-header>

              <v-expansion-panel-content>
                <v-row dense>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-if="editingSection === 'boundaries'"
                      v-model="editForm.boundaries.north"
                      :label="__('Norte')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Norte") }}
                      </div>

                      <div>
                        {{ boundaries.north || "-" }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      v-if="editingSection === 'boundaries'"
                      v-model="editForm.boundaries.south"
                      :label="__('Sul')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Sul") }}
                      </div>

                      <div>
                        {{ boundaries.south || "-" }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      v-if="editingSection === 'boundaries'"
                      v-model="editForm.boundaries.east"
                      :label="__('Leste')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Leste") }}
                      </div>

                      <div>
                        {{ boundaries.east || "-" }}
                      </div>
                    </template>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      v-if="editingSection === 'boundaries'"
                      v-model="editForm.boundaries.west"
                      :label="__('Oeste')"
                      outlined
                      dense
                    />

                    <template v-else>
                      <div class="text-caption grey--text">
                        {{ __("Oeste") }}
                      </div>

                      <div>
                        {{ boundaries.west || "-" }}
                      </div>
                    </template>
                  </v-col>
                </v-row>

                <ScopeProvider scope="leads.property_registry.update">
                  <div
                    v-if="editingSection === 'boundaries'"
                    class="section-actions"
                  >
                    <v-btn
                      text
                      small
                      class="mr-2"
                      :disabled="saving"
                      @click="resetSection('boundaries')"
                    >
                      {{ __("Cancelar") }}
                    </v-btn>

                    <v-btn
                      small
                      color="primary"
                      :loading="savingSection === 'boundaries'"
                      :disabled="saving"
                      @click="saveSection('boundaries')"
                    >
                      <v-icon left small> mdi-content-save </v-icon>

                      {{ __("Guardar alterações") }}
                    </v-btn>
                  </div>
                </ScopeProvider>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- ================================================= -->
            <!-- ALERTAS -->
            <!-- ================================================= -->

            <v-expansion-panel v-if="alerts.length">
              <v-expansion-panel-header>
                <div class="property-registry-section-title">
                  <v-icon> mdi-alert-outline </v-icon>

                  <span>
                    {{ __("Alertas") }}
                  </span>

                  <v-chip small class="ml-2">
                    {{ alerts.length }}
                  </v-chip>
                </div>
              </v-expansion-panel-header>

              <v-expansion-panel-content>
                <v-alert
                  v-for="alert in alerts"
                  :key="alert.id"
                  :type="alertType(alert.severity)"
                  outlined
                  dense
                  class="mb-2"
                >
                  <div class="font-weight-medium">
                    {{ alert.message }}
                  </div>

                  <div v-if="alert.field" class="text-caption mt-1">
                    {{ alert.field }}
                  </div>
                </v-alert>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </template>
    </template>

    <ConfirmationDialog ref="confirmDialog" />
  </v-card>
</template>

<script>
import {
  index as indexPropertyRegistry,
  update as updatePropertyRegistry,
  destroy as destroyPropertyRegistry,
  reprocess as reprocessPropertyRegistry,
} from "@/services/property_registries/property_registries";

import { formatDate } from "@/tools/date";

import i18n from "@/mixins/i18n";
import ToastsMixin from "@/mixins/ToastsMixin.vue";
import ConfirmationDialog from "@/components/tools/ConfirmationDialog.vue";
import ScopeProvider from "@/components/ScopeProvider";

export default {
  name: "PropertyRegistryTab",

  components: {
    ConfirmationDialog,
    ScopeProvider,
  },

  mixins: [i18n, ToastsMixin],

  props: {
    lead: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      i18n_prefix: "property_registry",
      propertyRegistries: [],
      propertyRegistry: null,
      selectedRegistryId: null,
      loading: false,
      viewing: false,
      reprocessing: false,
      deleting: false,
      saving: false,
      editingSection: null,
      editForm: {},
      savingSection: null,
      formatDate,
      openPanels: [0, 1, 2, 3, 4, 5, 6, 7, 8],
    };
  },

  computed: {
    identification() {
      return (
        (this.propertyRegistry && this.propertyRegistry.identification) || {
          district: null,
          municipality: null,
          parish: null,
          property_article_number: null,
          property_identification_number: null,
        }
      );
    },

    location() {
      return (this.propertyRegistry && this.propertyRegistry.location) || {};
    },

    description() {
      return (this.propertyRegistry && this.propertyRegistry.description) || {};
    },

    areas() {
      return (this.propertyRegistry && this.propertyRegistry.areas) || {};
    },

    valuation() {
      return (this.propertyRegistry && this.propertyRegistry.valuation) || {};
    },

    holders() {
      return (this.propertyRegistry && this.propertyRegistry.holders) || [];
    },

    additionalInformation() {
      return (
        (this.propertyRegistry &&
          this.propertyRegistry.additional_information) ||
        {}
      );
    },

    boundaries() {
      return (this.propertyRegistry && this.propertyRegistry.boundaries) || {};
    },

    alerts() {
      return (this.propertyRegistry && this.propertyRegistry.alerts) || [];
    },

    attachmentName() {
      if (!this.propertyRegistry || !this.propertyRegistry.attachment) {
        return "-";
      }

      return this.propertyRegistry.attachment.name || "-";
    },

    attachmentExtension() {
      if (!this.propertyRegistry || !this.propertyRegistry.attachment) {
        return "";
      }

      return this.propertyRegistry.attachment.extension
        ? this.propertyRegistry.attachment.extension.toUpperCase()
        : "";
    },

    statusLabel() {
      if (!this.propertyRegistry) {
        return "";
      }

      return this.getStatusLabel(this.propertyRegistry.status);
    },

    statusColor() {
      if (!this.propertyRegistry) {
        return "grey";
      }

      return this.getStatusColor(this.propertyRegistry.status);
    },

    statusTextColor() {
      if (!this.propertyRegistry) {
        return "white";
      }

      return this.getStatusTextColor(this.propertyRegistry.status);
    },

    taxAssetValue() {
      if (
        !this.valuation.current_tax_asset_value ||
        this.valuation.current_tax_asset_value.amount == null
      ) {
        return "-";
      }

      const amount = this.valuation.current_tax_asset_value.amount;

      const currency = this.valuation.current_tax_asset_value.currency || "EUR";

      return `${amount} ${currency}`;
    },
  },

  mounted() {
    this.fetchPropertyRegistry();
  },

  methods: {
    /**
     * Busca todas as cadernetas do lead.
     *
     * O service já retorna response.data.data,
     * portanto aqui esperamos diretamente um array.
     */
    async fetchPropertyRegistry() {
      this.loading = true;

      try {
        const response = await indexPropertyRegistry(this.lead.id);

        this.propertyRegistries = Array.isArray(response) ? response : [];

        if (!this.propertyRegistries.length) {
          this.propertyRegistry = null;
          return;
        }

        /*
         * Se já existe um registro selecionado,
         * tenta mantê-lo selecionado após o refresh.
         */
        if (this.propertyRegistry) {
          const current = this.propertyRegistries.find(
            (registry) => registry.id === this.propertyRegistry.id
          );

          this.propertyRegistry = current || this.propertyRegistries[0];
        } else {
          this.propertyRegistry = this.propertyRegistries[0];
        }

        this.selectedRegistryId = this.propertyRegistry.id;

        this.editingSection = null;
        this.editForm = {};
        this.savingSection = null;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Retorna o label de um status.
     */
    getStatusLabel(status) {
      const labels = {
        pending: this.__("Pendente", "property_registry"),
        processing: this.__("Em processamento", "property_registry"),
        completed: this.__("Concluído", "property_registry"),
        failed: this.__("Erro", "property_registry"),
        invalid_document: this.__("Documento inválido", "property_registry"),
      };

      return labels[status] || status;
    },

    /**
     * Retorna a cor de um status.
     */
    getStatusColor(status) {
      const colors = {
        pending: "grey",
        processing: "info",
        completed: "success",
        failed: "error",
        invalid_document: "warning",
      };

      return colors[status] || "grey";
    },

    /**
     * Retorna a cor do texto do chip.
     */
    getStatusTextColor(status) {
      return status === "pending" ? undefined : "white";
    },

    handleOpenDocument() {
      if (!this.propertyRegistry || !this.propertyRegistry.attachment) {
        return;
      }

      this.viewing = true;

      this.$emit("open-document", this.propertyRegistry.attachment);

      this.$nextTick(() => {
        this.viewing = false;
      });
    },

    async handleReprocess() {
      if (!this.propertyRegistry || this.propertyRegistry.status !== "failed") {
        return;
      }

      this.reprocessing = true;

      try {
        const response = await reprocessPropertyRegistry(
          this.lead.id,
          this.propertyRegistry.id
        );

        const updatedRegistry =
          response && response.data ? response.data : response;

        this.propertyRegistry = updatedRegistry;

        const index = this.propertyRegistries.findIndex(
          (registry) => registry.id === updatedRegistry.id
        );

        if (index !== -1) {
          this.$set(this.propertyRegistries, index, updatedRegistry);
        }

        this.toastSuccess(
          this.__(
            "Caderneta predial reenviada para processamento!",
            "property_registry"
          )
        );
      } catch (error) {
        const message = error.response?.data?.message || "";

        this.toastError(
          message ||
            this.__(
              "Não foi possível reprocessar a caderneta predial",
              "property_registry"
            )
        );
      } finally {
        this.reprocessing = false;
      }
    },

    async handleDelete() {
      if (!this.propertyRegistry) {
        return;
      }

      const registryId = this.propertyRegistry.id;

      this.$refs.confirmDialog.confirm(
        this.__("Excluir caderneta predial"),

        async ({ close, startLoading, stopLoading }) => {
          startLoading();

          try {
            await destroyPropertyRegistry(this.lead.id, registryId);

            /*
             * Remove da lista.
             */
            this.propertyRegistries = this.propertyRegistries.filter(
              (registry) => registry.id !== registryId
            );

            /*
             * Seleciona a próxima caderneta,
             * caso ainda exista alguma.
             */
            this.propertyRegistry = this.propertyRegistries.length
              ? this.propertyRegistries[0]
              : null;

            this.editing = false;
            this.editForm = {};
            this.savingSection = null;

            this.toastSuccess(
              this.__("Caderneta predial excluída!", "property_registry")
            );

            close();
          } catch (error) {
            const message = error.response?.data?.message || "";

            this.toastError(
              message ||
                this.__(
                  "Não foi possível excluir a caderneta predial",
                  "property_registry"
                )
            );
          } finally {
            stopLoading();
          }
        },

        null,

        {
          description: this.__(
            "Tem certeza que deseja excluir a caderneta predial?",
            "property_registry"
          ),

          confirmText: this.__("Excluir", "property_registry"),
          cancelText: this.__("Cancelar", "property_registry"),
          color: "error",
        }
      );
    },

    /**
     * Garante que objetos aninhados existam.
     */
    normalizeEditForm() {
      this.editForm.identification = this.editForm.identification || {};

      this.editForm.identification.district =
        this.editForm.identification.district || {};

      this.editForm.identification.municipality =
        this.editForm.identification.municipality || {};

      this.editForm.identification.parish =
        this.editForm.identification.parish || {};

      this.editForm.location = this.editForm.location || {};

      this.editForm.location.coordinates =
        this.editForm.location.coordinates || {};

      this.editForm.description = this.editForm.description || {};

      this.editForm.areas = this.editForm.areas || {};

      this.editForm.valuation = this.editForm.valuation || {};

      this.editForm.valuation.coordinates =
        this.editForm.valuation.coordinates || {};

      this.editForm.valuation.current_tax_asset_value =
        this.editForm.valuation.current_tax_asset_value || {};

      this.editForm.holders = this.editForm.holders || [];

      this.editForm.holders = this.editForm.holders.map((holder) => {
        holder.share = holder.share || {};

        holder.address = holder.address || {};

        return holder;
      });

      this.editForm.additional_information =
        this.editForm.additional_information || {};

      this.editForm.additional_information.tax_office =
        this.editForm.additional_information.tax_office || {};

      this.editForm.boundaries = this.editForm.boundaries || {};
    },

    /**
     * Restaura apenas uma seção.
     */
    resetSection(section) {
      if (!this.propertyRegistry) {
        return;
      }

      this.editForm[section] = JSON.parse(
        JSON.stringify(
          this.propertyRegistry[section] || (section === "holders" ? [] : {})
        )
      );

      this.normalizeEditForm();

      this.editingSection = null;
    },

    /**
     * Salva somente a seção solicitada.
     */
    async saveSection(section) {
      if (!this.propertyRegistry) {
        return;
      }

      this.savingSection = section;

      try {
        const payload = {
          [section]: this.editForm[section],
        };

        const response = await updatePropertyRegistry(
          this.lead.id,
          this.propertyRegistry.id,
          payload
        );

        if (response && response[section]) {
          this.$set(this.propertyRegistry, section, response[section]);

          /*
           * Mantém a lista sincronizada com o
           * registro selecionado.
           */
          const index = this.propertyRegistries.findIndex(
            (registry) => registry.id === this.propertyRegistry.id
          );

          if (index !== -1) {
            this.$set(this.propertyRegistries, index, this.propertyRegistry);
          }
        }

        this.editingSection = null;

        this.toastSuccess(this.__("Salvo!", "property_registry"));
      } catch (error) {
        const message = error.response?.data?.message || "";

        this.toastError(
          message ||
            this.__(
              "Não foi possível salvar as alterações",
              "property_registry"
            )
        );
      } finally {
        this.savingSection = null;
      }
    },

    alertType(severity) {
      const types = {
        INFO: "info",
        WARNING: "warning",
        ERROR: "error",
      };

      return types[severity] || "info";
    },

    startEditingSection(section) {
      if (!this.propertyRegistry || this.saving) {
        return;
      }

      this.editingSection = section;

      this.editForm = JSON.parse(JSON.stringify(this.propertyRegistry));

      this.normalizeEditForm();
    },
  },

  watch: {
    selectedRegistryId(newId) {
      if (!newId || !this.propertyRegistries.length) {
        return;
      }

      const registry = this.propertyRegistries.find(
        (item) => item.id === newId
      );

      if (!registry) {
        return;
      }

      this.propertyRegistry = registry;
      this.editingSection = null;
      this.editForm = {};
      this.savingSection = null;
    },
  },
};
</script>

<style scoped>
.property-registry-section-title {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
}

.property-registry-section-title .v-icon {
  margin-right: 8px;
}

.property-registry-item {
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;
}

.property-registry-item:hover {
  background-color: #fafafa;
}

.property-registry-item--selected {
  border-width: 2px;
}

.section-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #eeeeee;
}
</style>
